import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, GitCompareArrows, Crown, Bug, Droplets, Scale, Heart, AlertTriangle, Lock, Download, Package, Users } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ label, valueA, valueB, icon: Icon, format }) => {
  const fmt = (v) => {
    if (v === null || v === undefined || v === '') return '—';
    if (format === 'bool') return v ? 'Yes' : 'No';
    if (format === 'list') return Array.isArray(v) && v.length > 0 ? v.join(', ') : 'None';
    return String(v);
  };
  const a = fmt(valueA);
  const b = fmt(valueB);
  const highlight = a !== '—' && b !== '—' && a !== b;

  return (
    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 py-2.5 border-b border-stone-100 last:border-0">
      <div className={`text-right text-sm font-medium ${highlight ? 'text-amber-700' : 'text-stone-700'}`} data-testid={`compare-val-a-${label.toLowerCase().replace(/\s/g,'-')}`}>{a}</div>
      <div className="flex items-center gap-1.5 min-w-[130px] justify-center">
        {Icon && <Icon className="h-3.5 w-3.5 text-stone-400" />}
        <span className="text-xs text-stone-500 font-medium text-center">{label}</span>
      </div>
      <div className={`text-left text-sm font-medium ${highlight ? 'text-amber-700' : 'text-stone-700'}`} data-testid={`compare-val-b-${label.toLowerCase().replace(/\s/g,'-')}`}>{b}</div>
    </div>
  );
};

const WeightChart = ({ weightsA, weightsB, labelA, labelB }) => {
  if ((!weightsA || weightsA.length === 0) && (!weightsB || weightsB.length === 0)) {
    return <p className="text-xs text-stone-400 italic text-center py-4">No weight data recorded for either hive</p>;
  }
  const allWeights = [...(weightsA || []).map(w => w.weight), ...(weightsB || []).map(w => w.weight)].filter(Boolean);
  const maxW = Math.max(...allWeights, 1);

  return (
    <div className="space-y-3">
      <div className="text-xs font-semibold text-stone-600 mb-2">Weight History (last 10 records)</div>
      {[{ data: weightsA, label: labelA, color: 'bg-amber-500' }, { data: weightsB, label: labelB, color: 'bg-emerald-500' }].map(({ data, label, color }) => (
        <div key={label}>
          <div className="text-xs text-stone-500 mb-1">{label}</div>
          {data && data.length > 0 ? (
            <div className="flex items-end gap-1 h-12">
              {data.map((w, i) => (
                <div key={i} className="flex flex-col items-center flex-1" title={`${w.weight}kg — ${w.date}`}>
                  <div className={`${color} rounded-t w-full min-w-[4px]`} style={{ height: `${(w.weight / maxW) * 100}%` }} />
                </div>
              ))}
            </div>
          ) : <p className="text-xs text-stone-400 italic">No data</p>}
        </div>
      ))}
    </div>
  );
};

const HiveComparison = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hives, setHives] = useState([]);
  const [hiveA, setHiveA] = useState('');
  const [hiveB, setHiveB] = useState('');
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [planBlocked, setPlanBlocked] = useState(false);

  useEffect(() => {
    fetchHives();
  }, []);

  const fetchHives = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/hives`, { headers: { Authorization: `Bearer ${token}` } });
      setHives((res.data || []).filter(h => h.status !== 'destroyed'));
    } catch { /* empty */ }
  };

  const runComparison = async () => {
    if (!hiveA || !hiveB) { toast.error('Select two hives to compare'); return; }
    if (hiveA === hiveB) { toast.error('Select two different hives'); return; }
    setLoading(true);
    setPlanBlocked(false);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/hives/compare/${hiveA}/${hiveB}`, { headers: { Authorization: `Bearer ${token}` } });
      setComparison(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setPlanBlocked(true);
      } else {
        toast.error('Failed to load comparison');
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    if (!comparison) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/hives/compare/pdf`, comparison, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `hive_comparison_${comparison.hive_a?.hive_number}_vs_${comparison.hive_b?.hive_number}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to generate PDF');
    }
  };

  if (planBlocked) {
    return (
      <div className="bg-stone-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center space-y-4" data-testid="plan-gate">
          <Lock className="h-12 w-12 text-amber-500 mx-auto" />
          <h2 className="text-xl font-bold text-stone-800">Upgrade to Compare Hives</h2>
          <p className="text-sm text-stone-600">
            Hive Comparison is available on the <strong>Hobby Beekeeper</strong> plan ($15/mo) and above.
            See which hives are thriving and spot problems early.
          </p>
          <div className="flex gap-3 justify-center pt-2">
            <Button variant="outline" onClick={() => navigate('/')} className="rounded-full">Back</Button>
            <Button onClick={() => navigate('/subscription')} className="rounded-full bg-amber-600 hover:bg-amber-700 text-white">View Plans</Button>
          </div>
        </div>
      </div>
    );
  }

  const a = comparison?.hive_a;
  const b = comparison?.hive_b;

  return (
    <div className="bg-stone-50">
      <div className="max-w-7xl p-4 sm:p-6 pb-24">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
            <GitCompareArrows className="h-6 w-6 text-amber-600" /> Hive Comparison
          </h1>
          <p className="text-sm text-stone-500 mt-1">Compare two hives side-by-side</p>
        </div>

        {/* Selectors */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4 mb-5">
          <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-end">
            <div>
              <label className="text-xs font-semibold text-stone-600 mb-1 block">Hive A</label>
              <Select value={hiveA} onValueChange={setHiveA}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="select-hive-a">
                  <SelectValue placeholder="Select hive..." />
                </SelectTrigger>
                <SelectContent>
                  {hives.map(h => (
                    <SelectItem key={h.id} value={h.id} disabled={h.id === hiveB}>
                      Hive {h.hive_number} {h.nickname ? `(${h.nickname})` : ''} — {h.yard || 'No yard'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-center pb-1">
              <span className="text-stone-400 font-bold text-lg">vs</span>
            </div>
            <div>
              <label className="text-xs font-semibold text-stone-600 mb-1 block">Hive B</label>
              <Select value={hiveB} onValueChange={setHiveB}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="select-hive-b">
                  <SelectValue placeholder="Select hive..." />
                </SelectTrigger>
                <SelectContent>
                  {hives.map(h => (
                    <SelectItem key={h.id} value={h.id} disabled={h.id === hiveA}>
                      Hive {h.hive_number} {h.nickname ? `(${h.nickname})` : ''} — {h.yard || 'No yard'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button
            onClick={runComparison}
            disabled={loading || !hiveA || !hiveB || hiveA === hiveB}
            className="w-full mt-4 h-11 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold"
            data-testid="compare-btn"
          >
            {loading ? 'Comparing...' : 'Compare Hives'}
          </Button>
        </div>

        {/* Results */}
        {comparison && a && b && (
          <div className="space-y-4">
            {/* Download PDF */}
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={downloadPDF}
                className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="download-pdf-btn"
              >
                <Download className="h-4 w-4 mr-2" /> Download PDF
              </Button>
            </div>
            {/* Hive Headers */}
            <div className="grid grid-cols-[1fr_auto_1fr] gap-3">
              <div className="bg-amber-100 border border-amber-300 rounded-xl p-3 text-center">
                <div className="text-lg font-bold text-amber-800">Hive {a.hive_number}</div>
                <div className="text-xs text-amber-600">{a.nickname || a.yard}</div>
                <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full font-medium ${a.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{a.status}</span>
              </div>
              <div className="flex items-center"><GitCompareArrows className="h-5 w-5 text-stone-300" /></div>
              <div className="bg-emerald-100 border border-emerald-300 rounded-xl p-3 text-center">
                <div className="text-lg font-bold text-emerald-800">Hive {b.hive_number}</div>
                <div className="text-xs text-emerald-600">{b.nickname || b.yard}</div>
                <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full font-medium ${b.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{b.status}</span>
              </div>
            </div>

            {/* General Info */}
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4">
              <h3 className="text-sm font-bold text-stone-700 mb-2">General Info</h3>
              <StatCard label="Hive Type" valueA={a.hive_type} valueB={b.hive_type} />
              <StatCard label="Bee Variety" valueA={a.bee_variety} valueB={b.bee_variety} />
              <StatCard label="Apiary" valueA={a.yard} valueB={b.yard} />
              <StatCard label="Inspections" valueA={a.total_inspections} valueB={b.total_inspections} />
              <StatCard label="Last Inspected" valueA={a.last_inspection_date?.split('T')[0]} valueB={b.last_inspection_date?.split('T')[0]} />
            </div>

            {/* Queen & Colony */}
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4">
              <h3 className="text-sm font-bold text-stone-700 mb-2">Queen & Colony</h3>
              <StatCard label="Queen Spotted" valueA={a.queen_spotted} valueB={b.queen_spotted} icon={Crown} format="bool" />
              <StatCard label="Queen Marked" valueA={a.queen_marked} valueB={b.queen_marked} icon={Crown} format="bool" />
              <StatCard label="Queen Cells" valueA={a.queen_cells} valueB={b.queen_cells} icon={AlertTriangle} format="bool" />
              <StatCard label="Brood Pattern" valueA={a.brood_pattern} valueB={b.brood_pattern} icon={Heart} />
              <StatCard label="Population" valueA={a.population} valueB={b.population} />
              <StatCard label="Colony Mood" valueA={a.colony_mood} valueB={b.colony_mood} />
            </div>

            {/* Resources */}
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4">
              <h3 className="text-sm font-bold text-stone-700 mb-2">Resources & Stores</h3>
              <StatCard label="Nectar Level" valueA={a.nectar_level} valueB={b.nectar_level} icon={Droplets} />
              <StatCard label="Pollen Level" valueA={a.pollen_level} valueB={b.pollen_level} icon={Droplets} />
              <StatCard label="Honey Frames" valueA={a.honey_frames} valueB={b.honey_frames} />
              <StatCard label="Swarm Risk" valueA={a.swarm_conditions} valueB={b.swarm_conditions} icon={AlertTriangle} format="bool" />
            </div>

            {/* Health */}
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4">
              <h3 className="text-sm font-bold text-stone-700 mb-2">Health & Pests</h3>
              <StatCard label="Diseases" valueA={a.diseases} valueB={b.diseases} icon={Bug} format="list" />
              <StatCard label="Pests" valueA={a.pests} valueB={b.pests} icon={Bug} format="list" />
              <StatCard label="Mite Count" valueA={a.mite_count} valueB={b.mite_count} icon={Bug} />
            </div>

            {/* Harvest */}
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4">
              <h3 className="text-sm font-bold text-stone-700 mb-2">Harvest</h3>
              <StatCard label="Total Harvests" valueA={a.total_harvests} valueB={b.total_harvests} icon={Package} />
              <StatCard label="Total Yield (kg)" valueA={a.total_harvest_kg} valueB={b.total_harvest_kg} icon={Package} />
              <StatCard label="Frames Harvested" valueA={a.total_frames_harvested} valueB={b.total_frames_harvested} />
              <StatCard label="Last Harvest" valueA={a.last_harvest_date?.split('T')[0]} valueB={b.last_harvest_date?.split('T')[0]} />
            </div>

            {/* Team */}
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4">
              <h3 className="text-sm font-bold text-stone-700 mb-2">Team</h3>
              <StatCard label="Top Inspector" valueA={a.top_inspector} valueB={b.top_inspector} icon={Users} />
              <StatCard label="Inspections by Top" valueA={a.top_inspector_count} valueB={b.top_inspector_count} />
            </div>

            {/* Weight Trend */}
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4">
              <WeightChart
                weightsA={a.weight_history}
                weightsB={b.weight_history}
                labelA={`Hive ${a.hive_number}`}
                labelB={`Hive ${b.hive_number}`}
              />
            </div>

            {/* Treatment History */}
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4">
              <h3 className="text-sm font-bold text-stone-700 mb-2">Recent Treatments</h3>
              <div className="grid grid-cols-2 gap-4">
                {[{ hive: a, color: 'amber' }, { hive: b, color: 'emerald' }].map(({ hive: h, color }) => (
                  <div key={h.id}>
                    <div className={`text-xs font-semibold text-${color}-700 mb-1`}>Hive {h.hive_number}</div>
                    {h.treatment_history?.length > 0 ? h.treatment_history.map((t, i) => (
                      <div key={i} className="text-xs text-stone-600 py-1 border-b border-stone-50">
                        <span className="font-medium">{t.treatment}</span>
                        <span className="text-stone-400 ml-1">— {t.date?.split('T')[0]}</span>
                      </div>
                    )) : <p className="text-xs text-stone-400 italic">No treatments recorded</p>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {!comparison && !loading && (
          <div className="text-center py-16 text-stone-400">
            <GitCompareArrows className="h-16 w-16 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Select two hives above and click Compare</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HiveComparison;
